import {
  ButtonsTheme as ButtonsDefaultTheme,
  ButtonVariant,
} from "@acin.reactjs/acin-ui/button";
import {
  applyIconColor,
  ComponentCSSStyle,
  getMaxWidthMediaQuery,
} from "@acin.reactjs/acin-ui/theme";
import { DefaultTheme } from "styled-components";

const ButtonsTheme = (
  theme: DefaultTheme,
): ComponentCSSStyle<ButtonsDefaultTheme, ButtonVariant> => ({
  root: {
    borderRadius: "4px 20px 20px 16px",

    "&:focus-within, focus": {
      boxShadow: "none !important",
    },
  },
  label: {
    lineHeight: "14px",
  },
  variants: {
    secondaryButtonMarginRight: {
      root: {
        marginRight: "10px",

        [getMaxWidthMediaQuery(theme.breakpoints.sm)]: {
          marginBottom: "10px",
        },
      },
    },
    primary: {
      label: {
        color: theme.palette.common.black,
      },
      hover: {
        label: {
          color: theme.palette.common.black,
        },
        icon: applyIconColor(theme.palette.common.black),
      },
      active: {
        label: {
          color: theme.palette.common.black,
        },
      },
      icon: applyIconColor(theme.palette.common.black),
    },
    secondary: {
      root: {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.greys[300]}`,
      },
      label: {
        color: theme.palette.common.black,
      },
      hover: {
        root: {
          backgroundColor: theme.palette.greys[200],
        },
        label: {
          color: theme.palette.common.black,
        },
      },
      active: {
        root: {
          backgroundColor: theme.palette.greys[200],
          border: `1px solid ${theme.palette.greys[300]}`,
        },
        label: {
          color: theme.palette.common.black,
        },
      },
    },
    greenSecondary: {
      root: {
        backgroundColor: theme.palette.secondary[500],
        border: `1px solid ${theme.palette.secondary[500]}`,
      },
      label: {
        color: theme.palette.common.black,
      },
      hover: {
        root: {
          backgroundColor: theme.palette.secondary[600],
          border: `1px solid ${theme.palette.secondary[600]}`,
        },
        label: {
          color: theme.palette.common.black,
        },
      },
    },
    delete: {
      root: {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.status.error[500]}`,
      },
      label: {
        color: theme.palette.status.error[500],
      },
      hover: {
        root: {
          backgroundColor: theme.palette.status.error[100],
        },
        label: {
          color: theme.palette.status.error[500],
        },
      },
      active: {
        root: {
          backgroundColor: theme.palette.status.error[100],
          border: `1px solid ${theme.palette.status.error[500]}`,
        },
        label: {
          color: theme.palette.status.error[500],
        },
      },
    },
    secondaryTransparent: {
      root: {
        backgroundColor: "transparent",
      },
      hover: {
        root: {
          backgroundColor: theme.palette.greys[100],
          border: `1px solid ${theme.palette.greys[300]}`,
        },
      },
      active: {
        root: {
          backgroundColor: theme.palette.greys[100],
          border: `1px solid ${theme.palette.greys[300]}`,
        },
      },
    },
    deleteTransparent: {
      root: {
        backgroundColor: "transparent",
        border: `1px solid ${theme.palette.status.error[500]}`,
      },
      label: {
        color: theme.palette.status.error[500],
      },
      hover: {
        root: {
          backgroundColor: theme.palette.status.error[100],
          border: `1px solid ${theme.palette.status.error[500]}`,
        },
        label: {
          color: theme.palette.status.error[500],
        },
      },
      active: {
        root: {
          backgroundColor: theme.palette.status.error[100],
          border: `1px solid ${theme.palette.status.error[500]}`,
        },
        label: {
          color: theme.palette.status.error[500],
        },
      },
    },
    publicPrimary: {
      root: {
        backgroundColor: theme.palette.secondary[500],
      },
      hover: {
        root: {
          backgroundColor: theme.palette.secondary[600],
        },
        icon: applyIconColor(theme.palette.common.black),
      },
      active: {
        root: {
          backgroundColor: theme.palette.secondary[700],
        },
      },
    },
    publicTertiary: {
      label: {
        color: theme.palette.greys[800],
      },
    },
    publicSecondary: {
      root: {
        borderColor: theme.palette.secondary[500],
      },
      icon: {
        width: "15px",
        ...applyIconColor(theme.palette.common.black),
      },
      hover: {
        root: {
          backgroundColor: theme.palette.secondary[50],
        },
        icon: applyIconColor(theme.palette.common.black),
      },
      focused: {
        root: {
          boxShadow: `0 0 0 2px ${theme.palette.secondary[100]}`,
        },
      },
    },
    publicAcin: {
      label: {
        color: theme.palette.greys[600],
      },
      icon: applyIconColor(theme.palette.greys[600]),
      hover: {
        label: {
          textDecoration: "none",
          color: theme.palette.greys[400],
        },
        icon: applyIconColor(theme.palette.greys[400]),
      },
      focused: {
        root: {
          boxShadow: "none",
        },
      },
    },
    alertTranslation: {
      label: {
        color: theme.palette.greys[600],
        padding: "2px 8px",
      },
      root: {
        backgroundColor: theme.palette.greys[50],
        padding: "2px 8px",
        borderRadius: "4px",
        border: "none",
      },
      hover: {
        label: {
          textDecoration: "none",
          color: theme.palette.common.black,
        },
        root: {
          background: theme.palette.greys[50],
        },
      },
      focused: {
        root: {
          boxShadow: "none",
          background: theme.palette.greys[50],
        },
        label: {
          textDecoration: "none",
          color: theme.palette.common.black,
        },
      },
      active: {
        label: {
          textDecoration: "none",
          color: theme.palette.common.black,
        },
      },
    },
    cleanSelection: {
      root: {
        padding: "0px 0px 0px 24px",
      },
      label: {
        color: theme.palette.common.black,
      },
      icon: applyIconColor(theme.palette.common.black),
      hover: {
        label: {
          textDecoration: "none",
          color: theme.palette.greys[600],
        },
        icon: applyIconColor(theme.palette.common.black),
      },
      focused: {
        root: {
          boxShadow: "none",
        },
      },
      active: {
        label: {
          textDecoration: "none",
        },
      },
    },
  },
});

export default ButtonsTheme;
